import { createFileRoute, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetAdminRatesQueryKey,
  getGetAdminSettingsQueryKey,
  useCreateRate,
  useGetAdminRates,
  useGetAdminSettings,
  useSetBayRate,
  useUpdateAdminSettings,
  useUpdateRate,
} from '../../../api/generated/endpoints/admin/admin'
import { getListRatesQueryKey } from '../../../api/generated/endpoints/rates/rates'
import { getGetAvailabilityQueryKey } from '../../../api/generated/endpoints/availability/availability'
import type {
  AdminRateRead,
  AdminRatesResponse,
  BayOverrideRead,
  RateBayRead,
} from '../../../api/generated/model'
import { apiProblems } from '../../../lib/api-error'
import { formatUYU } from '../../../lib/money'

/**
 * The price list, and what each bay charges for each line. Decision 0005.
 *
 * ONE CARD PER LINE, with its bays inside it. The first version had the list
 * on top and a "per bay" section underneath that repeated every line under
 * every bay -- so to answer "what does the cage charge for one person?" he had
 * to find the right bay, then the right row, then work out that a blank field
 * meant "the local's price". Now the line and every bay's price for it are in
 * one place, and each bay says in words where its price comes from.
 *
 * A bay's price is a choice between three things, not a field with a magic
 * empty value: the local's price, a price of its own, or not sold there. The
 * editor asks exactly that.
 *
 * Nothing here can change what somebody was already charged: every booking
 * carries its own price. That sentence is on screen, because "if I raise the
 * price, do last month's numbers move?" is the first question anybody asks.
 */
export const Route = createFileRoute('/_authed/_admin/admin/rates')({
  component: RatesPage,
})

const FOCUS =
  'focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900'
const PRIMARY = `min-h-12 rounded-md bg-slate-900 px-6 text-lg font-medium text-white hover:bg-slate-700 disabled:bg-slate-300 ${FOCUS}`
const SECONDARY = `min-h-12 rounded-md border-2 border-slate-300 bg-white px-5 text-lg hover:border-slate-500 ${FOCUS}`
/** Retiring is undone from the retired list, so it does not need a dialog --
 *  but it should not look like the thing to press either. */
const QUIET_DANGER = `min-h-12 rounded-md px-4 text-lg text-red-800 underline-offset-4 hover:underline ${FOCUS}`

function RatesPage() {
  const screen = useGetAdminRates()

  if (screen.isPending) return <p className="text-slate-600">Cargando…</p>
  if (screen.isError) {
    return (
      <p role="alert" className="text-red-700">
        No se pudieron cargar las tarifas.
      </p>
    )
  }

  const data = screen.data
  const active = data.rates.filter((rate) => rate.active)
  const retired = data.rates.filter((rate) => !rate.active)

  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <div>
        <Link to="/admin/settings" className="inline-flex min-h-11 items-center underline">
          &lsaquo;&nbsp; Volver a Ajustes
        </Link>
        <h1 className="mt-2 text-3xl font-semibold">Tarifas</h1>
        <p className="mt-2 text-slate-700">
          Lo que se vende y a cuánto, en pesos. Cambiar un precio nunca modifica lo que ya se le
          cobró a alguien: cada reserva guarda el suyo.
        </p>
      </div>

      <section className="space-y-4" aria-label="Tarifas en venta">
        {active.map((rate) => (
          <LineCard key={rate.id} rate={rate} data={data} />
        ))}
        <NewLine />
      </section>

      <CoachFee />

      {retired.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Retiradas</h2>
          <p className="text-base text-slate-600">
            No se venden, pero las reservas que se hicieron con ellas las conservan.
          </p>
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-300 bg-white">
            {retired.map((rate) => (
              <RetiredRow key={rate.id} rate={rate} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/** Every write returns the whole screen; the customer side reads it too. */
function useRefresh() {
  const queryClient = useQueryClient()
  return (data: AdminRatesResponse) => {
    queryClient.setQueryData(getGetAdminRatesQueryKey(), data)
    void queryClient.invalidateQueries({ queryKey: getListRatesQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetAvailabilityQueryKey() })
  }
}

function Problems({ error }: { error: unknown }) {
  const problems = apiProblems(error, 'El cambio no se aplicó.')
  if (problems.length === 0) return null
  return (
    <ul role="alert" className="space-y-1 rounded-md bg-red-50 p-4 text-base text-red-800">
      {problems.map((problem) => (
        <li key={problem}>{problem}</li>
      ))}
    </ul>
  )
}

/** "Por hora, de 2 a 4 personas, con profesor" -- the line in words. */
function describe(rate: AdminRateRead): string {
  const people =
    rate.min_people === rate.max_people
      ? `${rate.min_people} ${rate.min_people === 1 ? 'persona' : 'personas'}`
      : `de ${rate.min_people} a ${rate.max_people} personas`
  const how =
    rate.pricing === 'round' ? `Ronda de ${minutesInWords(rate.round_minutes ?? 0)}` : 'Por hora'
  return [how, people, rate.with_coach ? 'con profesor' : null].filter(Boolean).join(', ')
}

/** What the number next to a price is per: "por hora, cada persona". */
function unit(rate: AdminRateRead): string {
  const per = rate.pricing === 'round' ? 'la ronda' : 'por hora'
  return rate.per_person ? `${per}, cada persona` : per
}

function minutesInWords(minutes: number): string {
  if (minutes % 60 === 0) return minutes === 60 ? '1 hora' : `${minutes / 60} horas`
  return `${minutes} minutos`
}

// ---------------------------------------------------------------------------
// One line, and every bay's price for it
// ---------------------------------------------------------------------------

function LineCard({ rate, data }: { rate: AdminRateRead; data: AdminRatesResponse }) {
  const [editing, setEditing] = useState(false)
  const refresh = useRefresh()
  const update = useUpdateRate({ mutation: { retry: false, onSuccess: refresh } })

  if (editing) {
    return (
      <LineForm
        initial={rate}
        title={`Editar «${rate.name}»`}
        pending={update.isPending}
        error={update.error}
        onCancel={() => setEditing(false)}
        onSave={(values) =>
          update.mutate({ rateId: rate.id, data: values }, { onSuccess: () => setEditing(false) })
        }
      />
    )
  }

  return (
    <article className="rounded-lg border border-slate-300 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 p-5">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">{rate.name}</h2>
          <p className="text-base text-slate-600">{describe(rate)}</p>
        </div>
        <p className="text-right">
          <span className="block text-2xl font-semibold tabular-nums">
            {formatUYU(rate.price_uyu_pesos)}
          </span>
          <span className="text-sm text-slate-600">{unit(rate)}</span>
        </p>
      </div>

      {data.bays.length > 0 && (
        <ul
          className="border-t border-slate-200"
          aria-label={`Precio de «${rate.name}» en cada simulador`}
        >
          {data.bays.map((bay) => (
            <BayPrice
              key={bay.id}
              bay={bay}
              rate={rate}
              override={data.overrides.find((o) => o.bay_id === bay.id && o.rate_id === rate.id)}
            />
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
        <Problems error={update.error} />
        <button
          type="button"
          className={QUIET_DANGER}
          disabled={update.isPending}
          onClick={() => update.mutate({ rateId: rate.id, data: { active: false } })}
        >
          Retirar
        </button>
        <button type="button" className={SECONDARY} onClick={() => setEditing(true)}>
          Editar
        </button>
      </div>
    </article>
  )
}

type BayChoice = 'local' | 'own' | 'none'

/** One bay's price for one line, said in words, with its editor folded in. */
function BayPrice({
  bay,
  rate,
  override,
}: {
  bay: RateBayRead
  rate: AdminRateRead
  override: BayOverrideRead | undefined
}) {
  const [open, setOpen] = useState(false)
  const refresh = useRefresh()
  const save = useSetBayRate({
    mutation: { retry: false, onSuccess: refresh },
  })

  const sold = override?.offered ?? true
  const own = sold ? (override?.price_uyu_pesos ?? null) : null
  const saved: BayChoice = !sold ? 'none' : own !== null ? 'own' : 'local'

  const [choice, setChoice] = useState<BayChoice>(saved)
  const [price, setPrice] = useState(own !== null ? String(own) : '')

  const status =
    saved === 'none' ? (
      <span className="text-slate-500">No se vende acá</span>
    ) : (
      <>
        <span className="font-semibold tabular-nums">{formatUYU(own ?? rate.price_uyu_pesos)}</span>{' '}
        <span className="text-sm text-slate-600">
          {saved === 'own' ? 'precio propio' : 'como el local'}
        </span>
      </>
    )

  if (!open) {
    return (
      <li className="flex min-h-14 items-center justify-between gap-4 px-5 py-2">
        <span className="min-w-0 truncate">{bay.name}</span>
        <span className="ml-auto text-right">{status}</span>
        <button
          type="button"
          onClick={() => {
            setChoice(saved)
            setPrice(own !== null ? String(own) : String(rate.price_uyu_pesos))
            setOpen(true)
          }}
          className={`min-h-11 rounded-md px-3 text-base underline underline-offset-4 hover:bg-slate-100 ${FOCUS}`}
          aria-label={`Cambiar el precio de «${rate.name}» en ${bay.name}`}
        >
          Cambiar
        </button>
      </li>
    )
  }

  const valid = choice !== 'own' || (price.trim() !== '' && Number(price) >= 0)

  return (
    <li className="bg-slate-50 px-5 py-4">
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate(
            {
              bayId: bay.id,
              rateId: rate.id,
              data:
                choice === 'none'
                  ? { price_uyu_pesos: null, offered: false }
                  : { price_uyu_pesos: choice === 'own' ? Number(price) : null, offered: true },
            },
            { onSuccess: () => setOpen(false) },
          )
        }}
      >
        <fieldset>
          <legend className="font-semibold">
            «{rate.name}» en {bay.name}
          </legend>
          <div className="mt-2 space-y-2">
            <Option
              name={`bay-${bay.id}-rate-${rate.id}`}
              checked={choice === 'local'}
              onChange={() => setChoice('local')}
            >
              Como el local, {formatUYU(rate.price_uyu_pesos)}
            </Option>
            <Option
              name={`bay-${bay.id}-rate-${rate.id}`}
              checked={choice === 'own'}
              onChange={() => setChoice('own')}
            >
              Un precio propio
            </Option>
            {choice === 'own' && (
              <label className="ml-12 flex items-center gap-2">
                <span className="sr-only">Precio en {bay.name}</span>
                <span aria-hidden="true" className="text-lg text-slate-600">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  autoFocus
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                  className={`min-h-12 w-40 rounded-md border-2 border-slate-400 bg-white px-3 text-lg tabular-nums ${FOCUS}`}
                />
                <span className="text-base text-slate-600">{unit(rate)}</span>
              </label>
            )}
            <Option
              name={`bay-${bay.id}-rate-${rate.id}`}
              checked={choice === 'none'}
              onChange={() => setChoice('none')}
            >
              No se vende en {bay.name}
            </Option>
          </div>
        </fieldset>
        <Problems error={save.error} />
        <div className="flex flex-wrap gap-2">
          <button type="submit" className={PRIMARY} disabled={save.isPending || !valid}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className={SECONDARY} onClick={() => setOpen(false)}>
            Cancelar
          </button>
        </div>
      </form>
    </li>
  )
}

/** A whole-row radio: the circle, the words, one 44px target. */
function Option({
  name,
  checked,
  onChange,
  children,
}: {
  name: string
  checked: boolean
  onChange: () => void
  children: React.ReactNode
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-white">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="size-6 accent-slate-900"
      />
      <span>{children}</span>
    </label>
  )
}

function RetiredRow({ rate }: { rate: AdminRateRead }) {
  const refresh = useRefresh()
  const update = useUpdateRate({ mutation: { retry: false, onSuccess: refresh } })
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
      <span>
        <span className="font-medium">{rate.name}</span>{' '}
        <span className="text-base text-slate-600">{formatUYU(rate.price_uyu_pesos)}</span>
      </span>
      <button
        type="button"
        className={SECONDARY}
        disabled={update.isPending}
        onClick={() => update.mutate({ rateId: rate.id, data: { active: true } })}
      >
        Volver a ofrecer
      </button>
      <Problems error={update.error} />
    </li>
  )
}

// ---------------------------------------------------------------------------
// Adding and editing a line
// ---------------------------------------------------------------------------

function NewLine() {
  const [open, setOpen] = useState(false)
  const refresh = useRefresh()
  const create = useCreateRate({ mutation: { retry: false, onSuccess: refresh } })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`min-h-14 w-full rounded-lg border-2 border-dashed border-slate-400 text-lg text-slate-700 hover:border-slate-600 hover:bg-white ${FOCUS}`}
      >
        + Agregar una tarifa
      </button>
    )
  }
  return (
    <LineForm
      title="Nueva tarifa"
      pending={create.isPending}
      error={create.error}
      onCancel={() => setOpen(false)}
      onSave={(values) => create.mutate({ data: values }, { onSuccess: () => setOpen(false) })}
    />
  )
}

type LineValues = {
  name: string
  pricing: 'hourly' | 'round'
  with_coach: boolean
  per_person: boolean
  min_people: number
  max_people: number
  price_uyu_pesos: number
  round_minutes: number | null
}

const FIELD = `min-h-12 rounded-md border-2 border-slate-400 bg-white px-3 text-lg ${FOCUS}`

function LineForm({
  initial,
  title,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial?: AdminRateRead
  title: string
  pending: boolean
  error: unknown
  onSave: (values: LineValues) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [pricing, setPricing] = useState<'hourly' | 'round'>(initial?.pricing ?? 'hourly')
  const [price, setPrice] = useState(String(initial?.price_uyu_pesos ?? ''))
  const [minPeople, setMinPeople] = useState(String(initial?.min_people ?? 1))
  const [maxPeople, setMaxPeople] = useState(String(initial?.max_people ?? 1))
  const [perPerson, setPerPerson] = useState(initial?.per_person ?? false)
  const [withCoach, setWithCoach] = useState(initial?.with_coach ?? false)
  const [roundMinutes, setRoundMinutes] = useState(String(initial?.round_minutes ?? 120))

  return (
    <form
      className="space-y-6 rounded-lg border-2 border-slate-900 bg-white p-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSave({
          name,
          pricing,
          with_coach: withCoach,
          per_person: perPerson,
          min_people: Number(minPeople),
          max_people: Number(maxPeople),
          price_uyu_pesos: Number(price),
          round_minutes: pricing === 'round' ? Number(roundMinutes) : null,
        })
      }}
    >
      <h2 className="text-xl font-semibold">{title}</h2>

      <label className="block">
        <span className="font-medium">Nombre</span>
        <span className="block text-base text-slate-600">Es lo que lee quien reserva.</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          className={`mt-1 w-full max-w-md ${FIELD}`}
        />
      </label>

      <Toggle
        legend="¿Cómo se cobra?"
        name="pricing"
        value={pricing}
        onChange={(next) => setPricing(next as 'hourly' | 'round')}
        options={[
          { value: 'hourly', label: 'Por hora' },
          { value: 'round', label: 'Por ronda' },
        ]}
      />

      {pricing === 'round' && (
        <label className="block">
          <span className="font-medium">¿Cuánto ocupa el simulador?</span>
          <span className="block text-base text-slate-600">
            En minutos: 120 son dos horas. Terminar antes no la abarata.
          </span>
          <span className="mt-1 flex items-center gap-2">
            <input
              type="number"
              min={10}
              step={10}
              value={roundMinutes}
              onChange={(event) => setRoundMinutes(event.target.value)}
              className={`w-28 tabular-nums ${FIELD}`}
            />
            <span className="text-base text-slate-600">minutos</span>
          </span>
        </label>
      )}

      <fieldset>
        <legend className="font-medium">¿Para cuántas personas?</legend>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-base">
          <label className="flex items-center gap-2">
            De
            <input
              type="number"
              min={1}
              value={minPeople}
              onChange={(event) => setMinPeople(event.target.value)}
              className={`w-20 tabular-nums ${FIELD}`}
            />
          </label>
          <label className="flex items-center gap-2">
            a
            <input
              type="number"
              min={1}
              value={maxPeople}
              onChange={(event) => setMaxPeople(event.target.value)}
              className={`w-20 tabular-nums ${FIELD}`}
            />
          </label>
        </div>
      </fieldset>

      <Toggle
        legend="¿El precio es por persona?"
        name="per_person"
        value={perPerson ? 'yes' : 'no'}
        onChange={(next) => setPerPerson(next === 'yes')}
        options={[
          { value: 'no', label: 'No, por el grupo' },
          { value: 'yes', label: 'Sí, cada una' },
        ]}
      />

      <label className="block">
        <span className="font-medium">
          Precio del local, {pricing === 'round' ? 'la ronda' : 'por hora'}
          {perPerson ? ', cada persona' : ''}
        </span>
        <span className="mt-1 flex items-center gap-2">
          <span aria-hidden="true" className="text-lg text-slate-600">
            $
          </span>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            className={`w-40 tabular-nums ${FIELD}`}
          />
        </span>
      </label>

      <Toggle
        legend="¿Con profesor?"
        hint="Con profesor, la plata es del profesor; el local cobra lo que paga él por hora de simulador."
        name="with_coach"
        value={withCoach ? 'yes' : 'no'}
        onChange={(next) => setWithCoach(next === 'yes')}
        options={[
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Sí' },
        ]}
      />

      <Problems error={error} />

      <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-5">
        <button type="submit" className={PRIMARY} disabled={pending || !name.trim() || !price}>
          {pending ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" className={SECONDARY} onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </form>
  )
}

/** Two or three big chips: the same radio idiom as the booking forms. */
function Toggle({
  legend,
  hint,
  name,
  value,
  onChange,
  options,
}: {
  legend: string
  hint?: string
  name: string
  value: string
  onChange: (next: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <fieldset>
      <legend className="font-medium">{legend}</legend>
      {hint && <p className="text-base text-slate-600">{hint}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="peer sr-only"
            />
            <span className="flex min-h-12 cursor-pointer items-center rounded-md border-2 border-slate-300 bg-white px-5 text-lg peer-not-checked:hover:border-slate-500 peer-checked:border-slate-900 peer-checked:bg-slate-900 peer-checked:text-white peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-slate-900">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

// ---------------------------------------------------------------------------
// The coach's bay fee
// ---------------------------------------------------------------------------

function CoachFee() {
  const settings = useGetAdminSettings()
  const queryClient = useQueryClient()
  const save = useUpdateAdminSettings({
    mutation: {
      retry: false,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() }),
    },
  })
  const current = settings.data?.coach_bay_fee_uyu_pesos
  const [fee, setFee] = useState<string | null>(null)
  const shown = fee ?? (current === undefined ? '' : String(current))

  return (
    <section className="rounded-lg border border-slate-300 bg-white p-5">
      <h2 className="text-xl font-semibold">Lo que paga un profesor</h2>
      <p className="mt-1 text-base text-slate-600">
        Por cada hora de simulador de una clase que cobra él; el resto de la clase es suyo. En $ 0,
        la clase entera es del profesor.
      </p>
      <form
        className="mt-4 flex flex-wrap items-center gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate(
            { data: { coach_bay_fee_uyu_pesos: Number(shown) } },
            { onSuccess: () => setFee(null) },
          )
        }}
      >
        <label className="flex items-center gap-2">
          <span className="sr-only">Lo que paga un profesor por hora</span>
          <span aria-hidden="true" className="text-lg text-slate-600">
            $
          </span>
          <input
            type="number"
            min={0}
            inputMode="numeric"
            value={shown}
            onChange={(event) => setFee(event.target.value)}
            className={`w-32 tabular-nums ${FIELD}`}
          />
          <span className="text-base text-slate-600">por hora</span>
        </label>
        <button
          type="submit"
          className={PRIMARY}
          disabled={save.isPending || current === undefined || Number(shown) === current}
        >
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        {/* A greyed button is not a confirmation; say it. */}
        {save.isSuccess && fee === null && (
          <span role="status" className="text-lg text-green-800">
            Guardado.
          </span>
        )}
      </form>
      <div className="mt-3">
        <Problems error={save.error} />
      </div>
    </section>
  )
}
